import React from "react";
import { useAppDispatch } from "../../framework/redux/hooks";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Grid2 from "@mui/material/Unstable_Grid2";
import { MainLayout } from "../layout/mainLayout";
import { changeOwnPassword, PasswordChangeDto } from "../../services/employeeService";
import { FormikProps, useFormik } from "formik";
import { defaultFormikConfig, formikFieldProps } from "../framework/formUtils";
import * as yup from "yup";
import { handleEasyOppApiError } from "../../services/baseService";
import { addSuccessMsg } from "../../framework/redux/slices/appSlice";
import { LoadingButton } from "../framework/loadingButton";
import { PageTitle } from "../layout/pageTitle";
import { InputAdornment } from "@mui/material";
import { IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

export const Profile = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [values, setValues] = React.useState({
        showOldPassword: false,
        showNewPassword: false,
        showNewPasswordAgain: false,
    });

    const handleClickShowPassword = (fieldName) => {
        if (fieldName === "oldPassword") {
            setValues({
                ...values,
                showOldPassword: !values.showOldPassword,
            });
        } else if (fieldName === "newPassword") {
            setValues({
                ...values,
                showNewPassword: !values.showNewPassword,
            });
        } else if (fieldName === "newPasswordAgain") {
            setValues({
                ...values,
                showNewPasswordAgain: !values.showNewPasswordAgain,
            });
        }
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleSubmit = (formData: PasswordChangeDto, { setSubmitting }) => {
        changeOwnPassword(formData)
            .then(() => {
                dispatch(addSuccessMsg(t("profile.passwordChange.passwordChangedSuccessfully")))
                navigate("/");
            })
            .catch(handleEasyOppApiError)
            .finally(() => setSubmitting(false));
    };

    const formik: FormikProps<PasswordChangeDto> = useFormik({
        ...defaultFormikConfig,
        initialValues: {
            oldPassword: "",
            newPassword: "",
        },
        validationSchema: yup.object({
            oldPassword: yup.string()
                .required(t("validation.fieldIsRequired", {
                    field: t("profile.passwordChange.oldPassword"),
                })),
            newPassword: yup.string()
                .required(t("validation.fieldIsRequired", {
                    field: t("profile.passwordChange.newPassword"),
                }))
                .min(8, t("validation.fieldMinLength", {
                    minLength: 8,
                }))
                .test("strong-password", t("validation.passwordNotSecureEnough"), (value) => {
                    const uppercaseRegex = /[A-Z]/;
                    const lowercaseRegex = /[a-z]/;
                    const specialCharacterRegex = /[!@#$%^&*(),.?":{}|<>]/;
                    const numberRegex = /[0-9]/;

                    const hasUppercase = uppercaseRegex.test(value);
                    const hasLowercase = lowercaseRegex.test(value);
                    const hasSpecialOrNumber = specialCharacterRegex.test(value) || numberRegex.test(value);
                    return hasUppercase && hasLowercase && hasSpecialOrNumber;
                }),
            newPasswordConfirmation: yup.string()
                .required(t("validation.fieldIsRequired", {
                    field: t("profile.passwordChange.newPasswordConfirmation"),
                }))
                .test("password-confirmation-equality", t("validation.newPasswordConfirmationDoesNotMatch"), (value) => {
                    return value === formik.values.newPassword;
                }),
        }),
        onSubmit: handleSubmit,
    });

    return (
        <MainLayout
            topComponent={
                <PageTitle header={t("profile.passwordChange.changePassword")} />
            }
            hasPx
        >
            <form onSubmit={formik.handleSubmit}>
                <Grid2 container gap={2}>
                    <TextField
                        required
                        fullWidth
                        label={t("profile.passwordChange.oldPassword")}
                        type={
                            values.showOldPassword
                                ? "text"
                                : "password"
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                            values.showOldPassword ?
                                                t("settings.passwordVisibilityHide") :
                                                t("settings.passwordVisibilityShow")
                                        }
                                        onClick={() => handleClickShowPassword("oldPassword")}
                                        onMouseDown={handleMouseDownPassword}
                                        >
                                        {values.showOldPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        {...formikFieldProps(formik, "oldPassword")}
                    />
                    <TextField
                        required
                        fullWidth
                        label={t("profile.passwordChange.newPassword")}
                        type={
                            values.showNewPassword
                                ? "text"
                                : "password"
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                            values.showNewPassword ?
                                                t("settings.passwordVisibilityHide") :
                                                t("settings.passwordVisibilityShow")
                                        }
                                        onClick={() => handleClickShowPassword("newPassword")}
                                        onMouseDown={handleMouseDownPassword}
                                        >
                                        {values.showNewPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        {...formikFieldProps(formik, "newPassword")}
                    />
                    <TextField
                        required
                        fullWidth
                        label={t("profile.passwordChange.newPasswordConfirmation")}
                        type={
                            values.showNewPasswordAgain
                                ? "text"
                                : "password"
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                            values.showNewPasswordAgain ?
                                                t("settings.passwordVisibilityHide") :
                                                t("settings.passwordVisibilityShow")
                                        }
                                        onClick={() => handleClickShowPassword("newPasswordAgain")}
                                        onMouseDown={handleMouseDownPassword}
                                        >
                                        {values.showNewPasswordAgain ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        {...formikFieldProps(formik, "newPasswordConfirmation")}
                    />
                    <Grid2 xs={12} display="flex" justifyContent="center">
                        <LoadingButton type="submit" variant="contained" fullWidth loading={formik.isSubmitting}>
                            {t("profile.passwordChange.setPassword")}
                        </LoadingButton>
                    </Grid2>
                </Grid2>
            </form>
        </MainLayout>
    );
};
